import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';

type LoaderMode = 'loading' | 'success' | 'error';
type Phase = 'spinner' | 'transition' | 'final';
interface VerificationLoaderProps {
  isVisible: boolean;
  isMobile?: boolean;
  mode: LoaderMode;
  onErrorComplete?: () => void;
  onSuccessComplete?: () => void;
  overlay?: boolean;
  targetId?: string;
  testInstantComplete?: boolean;
}

const successColor = '#22c55e';
const errorColor = '#ef4444';
const neutralTrack = '#e5e7eb';
const neutralArc = '#9ca3af';

// Visuals for each phase
const Spinner = () => (
  <motion.svg width="84" height="84" viewBox="0 0 84 84" animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1.1, ease: 'linear' }}>
    <circle cx="42" cy="42" r="38" fill="none" stroke={neutralTrack} strokeWidth="2" opacity="0.25" />
    <circle cx="42" cy="42" r="38" fill="none" stroke={neutralArc} strokeWidth="2" strokeLinecap="round" strokeDasharray="60 200" />
  </motion.svg>
);

const FinalError = () => (
  <div className="flex flex-col items-center justify-center text-white gap-4">
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-label="Verification failed">
      <path
        d="M11.5283 1.5999C11.7686 1.29437 12.2314 1.29437 12.4717 1.5999L14.2805 3.90051C14.4309 4.09173 14.6818 4.17325 14.9158 4.10693L17.7314 3.3089C18.1054 3.20292 18.4799 3.475 18.4946 3.86338L18.6057 6.78783C18.615 7.03089 18.77 7.24433 18.9984 7.32823L21.7453 8.33761C22.1101 8.47166 22.2532 8.91189 22.0368 9.23478L20.4078 11.666C20.2724 11.8681 20.2724 12.1319 20.4078 12.334L22.0368 14.7652C22.2532 15.0881 22.1101 15.5283 21.7453 15.6624L18.9984 16.6718C18.77 16.7557 18.615 16.9691 18.6057 17.2122L18.4946 20.1366C18.4799 20.525 18.1054 20.7971 17.7314 20.6911L14.9158 19.8931C14.6818 19.8267 14.4309 19.9083 14.2805 20.0995L12.4717 22.4001C12.2314 22.7056 11.7686 22.7056 11.5283 22.4001L9.71949 20.0995C9.56915 19.9083 9.31823 19.8267 9.08421 19.8931L6.26856 20.6911C5.89463 20.7971 5.52014 20.525 5.50539 20.1366L5.39427 17.2122C5.38503 16.9691 5.22996 16.7557 5.00164 16.6718L2.25467 15.6624C1.88986 15.5283 1.74682 15.0881 1.96317 14.7652L3.59221 12.334C3.72761 12.1319 3.72761 11.8681 3.59221 11.666L1.96317 9.23478C1.74682 8.91189 1.88986 8.47166 2.25467 8.33761L5.00165 7.32823C5.22996 7.24433 5.38503 7.03089 5.39427 6.78783L5.50539 3.86338C5.52014 3.475 5.89463 3.20292 6.26857 3.3089L9.08421 4.10693C9.31823 4.17325 9.56915 4.09173 9.71949 3.90051L11.5283 1.5999Z"
        stroke="currentColor"
        strokeWidth="1.272"
        strokeLinejoin="round"
      />
      <path
        d="M8.75 8.75l6.5 6.5M15.25 8.75l-6.5 6.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
    <h3 className="text-white text-2xl font-semibold leading-tight text-center">
      Verification
      <br />
      Failed
    </h3>
  </div>
);

const FinalSuccess = () => (
  <div className="flex flex-col items-center justify-center text-white">
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-label="Verification failed">
      <path
        d="M11.5283 1.5999C11.7686 1.29437 12.2314 1.29437 12.4717 1.5999L14.2805 3.90051C14.4309 4.09173 14.6818 4.17325 14.9158 4.10693L17.7314 3.3089C18.1054 3.20292 18.4799 3.475 18.4946 3.86338L18.6057 6.78783C18.615 7.03089 18.77 7.24433 18.9984 7.32823L21.7453 8.33761C22.1101 8.47166 22.2532 8.91189 22.0368 9.23478L20.4078 11.666C20.2724 11.8681 20.2724 12.1319 20.4078 12.334L22.0368 14.7652C22.2532 15.0881 22.1101 15.5283 21.7453 15.6624L18.9984 16.6718C18.77 16.7557 18.615 16.9691 18.6057 17.2122L18.4946 20.1366C18.4799 20.525 18.1054 20.7971 17.7314 20.6911L14.9158 19.8931C14.6818 19.8267 14.4309 19.9083 14.2805 20.0995L12.4717 22.4001C12.2314 22.7056 11.7686 22.7056 11.5283 22.4001L9.71949 20.0995C9.56915 19.9083 9.31823 19.8267 9.08421 19.8931L6.26856 20.6911C5.89463 20.7971 5.52014 20.525 5.50539 20.1366L5.39427 17.2122C5.38503 16.9691 5.22996 16.7557 5.00164 16.6718L2.25467 15.6624C1.88986 15.5283 1.74682 15.0881 1.96317 14.7652L3.59221 12.334C3.72761 12.1319 3.72761 11.8681 3.59221 11.666L1.96317 9.23478C1.74682 8.91189 1.88986 8.47166 2.25467 8.33761L5.00165 7.32823C5.22996 7.24433 5.38503 7.03089 5.39427 6.78783L5.50539 3.86338C5.52014 3.475 5.89463 3.20292 6.26857 3.3089L9.08421 4.10693C9.31823 4.17325 9.56915 4.09173 9.71949 3.90051L11.5283 1.5999Z"
        stroke="currentColor"
        strokeWidth="1.272"
        strokeLinejoin="round"
      />
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    <h3 className="text-base font-semibold">Verified</h3>
  </div>
);

 // Expanding circle that fills the container
type AnimationControlsType = ReturnType<typeof useAnimation>;
// Shared typed shape for grouped animation controls
type LoaderAnimations = {
  spinner: AnimationControlsType;
  outline: AnimationControlsType;
  dot: AnimationControlsType;
  solid: AnimationControlsType;
  expander: AnimationControlsType;
  bg: AnimationControlsType;
  card: AnimationControlsType;
  final: AnimationControlsType;
};
const Expander: React.FC<{ color: string; ec: AnimationControlsType }> = ({ color, ec }) => (
  <motion.div
    className="rounded-full"
    style={{ width: 84, height: 84, backgroundColor: color }}
    animate={ec}
  />
);

// Extracted presentational fragments to keep main component low in cognitive complexity
interface BuildStageProps { phase: Phase; animations: LoaderAnimations; mode: LoaderMode }
const BuildStage: React.FC<BuildStageProps> = ({ phase, animations, mode }) => (
  <div className="relative w-full">
    <div className="relative flex items-center justify-center py-8" style={{ minHeight: 120 }}>
      <AnimatePresence>
        {phase === 'spinner' && (
          <motion.div key="spin" animate={animations.spinner}>
            <Spinner />
          </motion.div>
        )}
      </AnimatePresence>
      {phase !== 'final' && phase !== 'spinner' && (
        <div className="absolute inset-0 grid place-items-center">
          <motion.svg width="84" height="84" viewBox="0 0 84 84" animate={animations.outline} className="absolute">
            <circle cx="42" cy="42" r="38" fill="none" stroke={mode === 'error' ? errorColor : successColor as string} strokeWidth="2" />
          </motion.svg>
          <motion.div className="rounded-full absolute" style={{ width: 12, height: 12, backgroundColor: mode === 'error' ? errorColor : successColor as string }} animate={animations.dot} />
          <motion.div className="rounded-full absolute" style={{ width: 84, height: 84, backgroundColor: mode === 'error' ? errorColor : successColor as string }} animate={animations.solid} />
          <Expander color={mode === 'error' ? errorColor : successColor as string} ec={animations.expander} />
        </div>
      )}
    </div>
  </div>
);

interface FinalCardProps { animations: LoaderAnimations; mode: LoaderMode }
const FinalCard: React.FC<FinalCardProps> = ({ animations, mode }) => (
  <motion.div animate={animations.final}>
    {mode === 'success' ? <FinalSuccess /> : <FinalError />}
  </motion.div>
);

const VerificationLoader: React.FC<VerificationLoaderProps> = ({ isVisible, isMobile = false, mode, onErrorComplete, onSuccessComplete, overlay = true, targetId, testInstantComplete = false }) => {
  // Create animation controls individually so their identity is stable; avoid recreating a container object in deps
  // (previous implementation recreated an animations object each render causing effect cleanup & aborted sequence callbacks)
  const spinner = useAnimation();
  const outline = useAnimation();
  const dot = useAnimation();
  const solid = useAnimation();
  const expander = useAnimation();
  const bg = useAnimation();
  const card = useAnimation();
  const final = useAnimation();
  const animations: LoaderAnimations = { spinner, outline, dot, solid, expander, bg, card, final };
  const [phase, setPhase] = useState<Phase>('spinner');
  const finalizeStartedRef = useRef(false);

  const resetVisualState = useCallback((mobile: boolean) => {
    setPhase('spinner');
    finalizeStartedRef.current = false;
    spinner.set({ opacity: 1 });
    outline.set({ opacity: 0, scale: 0.9 });
    dot.set({ opacity: 0, scale: 0 });
    solid.set({ opacity: 0, scale: 0.6 });
    expander.set({ opacity: 0, scale: 0.14 });
    final.set({ opacity: 0, y: 8 });
    bg.set({ backgroundColor: mobile ? '#ffffff' : 'rgba(0,0,0,0)' });
  }, [spinner, outline, dot, solid, expander, final, bg]);

  // Reset on visibility
  useEffect(() => { if (isVisible) resetVisualState(isMobile); }, [isVisible, isMobile, resetVisualState]);

  const runSequence = useCallback(async (mobile: boolean, outcome: LoaderMode) => {
    const color = outcome === 'success' ? successColor : errorColor;
    const steps: Array<() => Promise<void>> = [
      () => spinner.start({ opacity: 0, transition: { duration: 0.16 } }).then(() => { setPhase('transition'); }),
      () => outline.start({ opacity: 1, scale: 1.05, transition: { duration: 0.16, ease: 'easeOut' } }),
      () => dot.start({ opacity: 1, scale: 1, transition: { duration: 0.16, ease: 'easeOut' } }),
      () => solid.start({ opacity: 1, scale: 1.14, transition: { duration: 0.24, ease: [0.34, 1.56, 0.64, 1] } }),
      () => expander.start({ opacity: 1, scale: 0.18, transition: { duration: 0.12 } }),
      () => expander.start({ scale: mobile ? 30 : 12, transition: { duration: 0.42, ease: [0.22, 1, 0.36, 1] } }),
      () => bg.start({ backgroundColor: color, transition: { duration: 0.12 } }),
      async () => { setPhase('final'); await final.start({ opacity: 1, y: 0, transition: { duration: 0.18 } }); }
    ];
    for (const step of steps) { await step(); }
  }, [spinner, outline, dot, solid, expander, bg, final]);

  // Orchestrate finalize sequence
  useEffect(() => {
    if (!isVisible || mode === 'loading' || finalizeStartedRef.current) return;
    finalizeStartedRef.current = true;
    if (testInstantComplete) {
      setPhase('final');
      setTimeout(() => { mode === 'success' ? onSuccessComplete?.() : onErrorComplete?.(); }, 0);
      return;
    }
    let aborted = false;
    (async () => {
      try {
        await runSequence(isMobile, mode);
        if (aborted) return;
        setTimeout(() => { if (!aborted) (mode === 'success' ? onSuccessComplete?.() : onErrorComplete?.()); }, 1000);
      } catch { /* swallow animation errors */ }
    })();
    return () => { aborted = true; };
  }, [isVisible, mode, isMobile, testInstantComplete, onSuccessComplete, onErrorComplete, runSequence]);

  if (!isVisible) return null;

  // Simple inline spinner only (no modal/backdrop/portal)
  if (!overlay) {
    return (
      <div className="flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const renderMobile = () => createPortal(
    <motion.div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden" animate={animations.bg} initial={{ backgroundColor: '#ffffff' }}>
      {phase === 'final' ? <FinalCard animations={animations} mode={mode} /> : <BuildStage phase={phase} animations={animations} mode={mode} />}
    </motion.div>,
    document.body
  );

  const renderTargetOverlay = (el: HTMLElement) => (
    <>
      {createPortal(
        <div className="fixed inset-0 z-40"><div className="absolute inset-0 bg-black bg-opacity-50" /></div>,
        document.body
      )}
      {createPortal(
        <div className="absolute inset-0 z-50">
          <motion.div className="absolute inset-0" animate={animations.bg} initial={{ backgroundColor: 'rgba(0,0,0,0)' }} />
          <div className="relative w-full h-full flex items-center justify-center">
            {phase === 'final' ? <FinalCard animations={animations} mode={mode} /> : <BuildStage phase={phase} animations={animations} mode={mode} />}
          </div>
        </div>,
        el
      )}
    </>
  );

  const renderDesktopFallback = () => createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black bg-opacity-50" />
      <motion.div className="relative z-10 w-full h-full flex items-center justify-center" animate={animations.bg} initial={{ backgroundColor: 'rgba(0,0,0,0)' }}>
        {phase === 'final' ? <FinalCard animations={animations} mode={mode} /> : <BuildStage phase={phase} animations={animations} mode={mode} />}
      </motion.div>
    </div>,
    document.body
  );

  // Mobile fullscreen container
  if (isMobile) return renderMobile();

  // Desktop
  // If a targetId is provided, mount overlay inside that element (replace card content visually)
  if (!isMobile && targetId) {
    const targetEl = typeof document !== 'undefined' ? document.getElementById(targetId) : null;
    if (targetEl) {
      // Ensure target container can host absolute overlay
      if (targetEl.style.position === '' || targetEl.style.position === 'static') {
        targetEl.style.position = 'relative';
      }
      return renderTargetOverlay(targetEl);
    }
  }

  return renderDesktopFallback();
};

export default VerificationLoader;
